import axios from 'axios';
import { getAuthHeader } from './authService';
import API_CONFIG from '../config';

const API_URL = `${API_CONFIG.BASE_URL}/units`;

/**
 * Service for managing units of measurement
 */
const unitService = {
  /**
   * Get all units with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.size - Page size
   * @param {string} params.sort - Field to sort by
   * @param {string} params.direction - Sort direction (ASC or DESC)
   * @returns {Promise<Object>} Paginated units data
   */
  getAllUnits: async (params = { page: 0, size: 10, sort: 'name', direction: 'ASC' }) => {
    try {
      const response = await axios.get(API_URL, {
        params,
        headers: getAuthHeader()
      });
      
      // Handle both paginated and non-paginated responses
      if (response.data && typeof response.data === 'object' && response.data.content) {
        // Return paginated response
        return response.data;
      } else if (Array.isArray(response.data)) {
        // If API returns an array directly (non-paginated)
        // Convert to paginated format for consistent handling in the UI
        return {
          content: response.data,
          totalElements: response.data.length,
          totalPages: 1,
          size: response.data.length,
          number: 0
        };
      } else {
        // Handle unexpected response format
        console.warn('Unexpected response format from units API:', response.data);
        return { content: [], totalElements: 0, totalPages: 0, size: params.size, number: params.page };
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      throw error;
    }
  },

  /**
   * Get units by company ID
   * @param {number} companyId - Company ID
   * @returns {Promise<Array>} List of units
   */
  getUnitsByCompany: async (companyId) => {
    const response = await axios.get(`${API_URL}/company/${companyId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Get a unit by ID
   * @param {number} id - Unit ID
   * @returns {Promise<Object>} Unit details
   */
  getUnitById: async (id) => {
    const response = await axios.get(`${API_URL}/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Create a new unit
   * @param {Object} unitData - Unit data
   * @returns {Promise<Object>} Created unit
   */
  createUnit: async (unitData) => {
    const response = await axios.post(API_URL, unitData, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Update a unit
   * @param {number} id - Unit ID
   * @param {Object} unitData - Updated unit data
   * @returns {Promise<Object>} Updated unit
   */
  updateUnit: async (id, unitData) => {
    const response = await axios.put(`${API_URL}/${id}`, unitData, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Delete a unit
   * @param {number} id - Unit ID
   * @returns {Promise<void>}
   */
  deleteUnit: async (id) => {
    await axios.delete(`${API_URL}/${id}`, {
      headers: getAuthHeader()
    });
  }
};

export default unitService; 